import React, {useMemo} from "react";
import {RemoteValue} from "../../../util/RemoteValue";
import {useEmployeeStore} from "../../../api/EmployeeStore";
import {useEmployeeKeyHistoryStore} from "../../../api/EmployeeKeyHistoryStore";
import {useResidenceStore} from "../../../api/ResidenceStore";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {Link as RouterLink} from "react-router-dom";
import {KeyChip} from "../../../components/key-chip/KeyChip";
import {Stack} from "@mui/material";
import EmployeeAvatar from "../../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../../components/employee-link/EmployeeLink";
import {SiteChip} from "../../../components/site-chip/SiteChip";
import {EmployeeHistoryButton} from "../../history-dialog/HistoryDialogContext";
import {Employees_Read, KeyData_Read, Offices_Read, WorkSites_Read} from "../../../util/access/Permission";
import {useToday} from "../../../util/DateUtil";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {Employee, KeySnapshot} from "../../../api/Model";
import {useWorkSiteMapStore, WorkSites} from "../../../api/WorkSiteMapStore";

type NonResidentWithKeysProblem = {employee: Employee, keys: KeySnapshot[]};

const nonResidentsWithKeysCsvModel = (workSites: WorkSites) => new CsvModel<NonResidentWithKeysProblem>([
    {header: 'Keys', value: ({keys}) => keys.map(({key}) => key.keyNumber).join(', ')},
    {header: 'Person', value: ({employee}) => employee.fullName},
    {header: 'Email', value: ({employee}) => employee.email},
    {header: 'Hibob Site', value: ({employee}) => workSites.residenceByEmployee(employee).workSite.name},
    {header: 'Hibob Office', value: ({employee}) => workSites.residenceByEmployee(employee).hibobOffice?.name ?? ''},
]);

export function NonResidentsWithKeysWidget() {
    const pageUrl = usePageUrl();
    const employees = useEmployeeStore(state => state.remoteValue);
    const keyHistory = useEmployeeKeyHistoryStore(state => state.remoteValue);
    const residency = useResidenceStore().remoteValue;
    const workSites = useWorkSiteMapStore(state => state.remoteValue);
    const today = useToday();
    const problems = useMemo(() => RemoteValue.combine({employees, keyHistory, residency})
        .map(values => values.employees.flatMap(employee => {
            const keys = values.keyHistory.active(employee)
                .filter(snapshot => snapshot.historyRecord.location.type !== 'with-employee' || snapshot.historyRecord.location.expTo === undefined || snapshot.historyRecord.location.expTo < today);
            return !employee.deactivated && !values.residency.isResident(employee) && keys.length > 0
                ? [{employee, keys}]
                : [];
        })), [employees, keyHistory, residency]);
    const csvModel = useMemo(() => workSites.map(
        workSites => nonResidentsWithKeysCsvModel(workSites)
    ), [workSites]);

    return (
        <DashboardWidget
            title="Non-residents with keys"
            description="Employees that do not belong to this office but still having a permament or temporary not returned on time key"
            problems={problems}
            requiredPermissions={[Employees_Read, KeyData_Read, Offices_Read, WorkSites_Read]}
            link={pageUrl(AppPages.employees, {}, {residency: -1001, hasKey: 'has-key', active: 'active'})}
            csvModel={csvModel}
            renderProblem={({employee, keys}, index) => (
                <DashboardWidgetItem
                    key={employee.id}
                    index={index}
                    left={
                        <>
                            <EmployeeAvatar employee={employee} sx={{width: 24, height: 24}}/>
                            <EmployeeLink employee={employee}/>
                            <SiteChip employee={employee}/>
                        </>
                    }
                    right={
                        <>
                            <Stack direction="row" gap={1} flexWrap="wrap">
                                {keys.map(({key, historyRecord}) => (
                                    <RouterLink key={key.id} to={pageUrl(AppPages.keys, {}, {search: key.keyNumber})}>
                                        <KeyChip keyData={key} record={historyRecord}/>
                                    </RouterLink>
                                ))}
                            </Stack>
                            <EmployeeHistoryButton employee={employee}/>
                        </>
                    }
                />
            )}
        />
    );
}
